import React from "react";

interface Step3Props {
    formData: any;
    handleArrayChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number, arrayName: string) => void;
    handleAddEntry: (arrayName: string, newEntry: any) => void;
    handleRemoveEntry: (arrayName: string, index: number) => void;
}

const Step3: React.FC<Step3Props> = ({ formData, handleArrayChange, handleAddEntry, handleRemoveEntry }) => {
    return (
        <div className="bg-white shadow-sm rounded-lg p-6">
            <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900">Skills & Certifications</h2>
                <p className="mt-1 text-sm text-gray-600">Add your technical skills and professional certifications.</p>
            </div>

            <div className="space-y-8">
                {/* Skills Section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-900">Skills</h3>
                        <button
                            type="button"
                            onClick={() => handleAddEntry("skills", "")}
                            className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Add Skill
                        </button>
                    </div>
                    <p className="text-sm text-gray-500">Add your technical and professional skills</p>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {formData.skills.map((skill, index) => (
                            <div key={index} className="relative group">
                                <input
                                    type="text"
                                    name="skill"
                                    placeholder="Enter a skill"
                                    value={skill}
                                    onChange={(e) => handleArrayChange(e, index, "skills")}
                                    className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemoveEntry("skills", index)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-500 hover:text-red-700 focus:outline-none"
                                    aria-label="Remove skill"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Certifications Section */}
                <div className="space-y-4 pt-8 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-900">Certifications</h3>
                        <button
                            type="button"
                            onClick={() => handleAddEntry("certifications", { name: "", issuingOrganization: "", date: "" })}
                            className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Add Certification
                        </button>
                    </div>
                    <p className="text-sm text-gray-500">Add any relevant certifications or professional qualifications</p>

                    {formData.certifications.map((cert, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-md space-y-4 hover:border-gray-300 transition-colors">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Certification Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="e.g., AWS Solutions Architect"
                                        value={cert.name}
                                        onChange={(e) => handleArrayChange(e, index, "certifications")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Issuing Organization</label>
                                    <input
                                        type="text"
                                        name="issuingOrganization"
                                        placeholder="e.g., Amazon Web Services"
                                        value={cert.issuingOrganization}
                                        onChange={(e) => handleArrayChange(e, index, "certifications")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Date Obtained</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={cert.date}
                                    onChange={(e) => handleArrayChange(e, index, "certifications")}
                                    className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemoveEntry("certifications", index)}
                                className="mt-2 inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Remove Certification
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Step3;